import { threadCopy } from '@/copy'
import { Avatar } from '@/ui/Avatar/Avatar'
import type { ComboboxOption } from '@/ui/Combobox/Combobox'
import {
  type ActiveToken,
  filterSlashCommands,
  formatMention,
  formatResource,
  type MentionRef,
  type ResourceRef,
} from '../../utils/tokens'

export interface MentionAgent {
  id: string
  name: string
}

/** A suggestion for the active `@`, `#` or `/` token. */
export type MentionOption = ComboboxOption & {
  /** The text that replaces the token. */
  insert: string
  /** Present for resource options: the picked resource. */
  resource?: ResourceRef
  /** Present for agent and user options: the picked mention. */
  mention?: MentionRef
}

/** The options for the active token, filtered and ordered: `@` lists agents,
 *  then users; `#` lists resources by name; `/` lists slash commands. */
export function mentionOptions(
  active: ActiveToken,
  agents: MentionAgent[],
  users: string[],
  resources: ResourceRef[],
): MentionOption[] {
  const q = active.query.toLowerCase()
  if (active.trigger === '@') {
    const agentOptions = agents
      .filter(
        (a) =>
          a.name.toLowerCase().includes(q) || a.id.toLowerCase().includes(q),
      )
      .map<MentionOption>((a) => {
        const insert = formatMention(a.name)
        return {
          id: `agent:${insert}`,
          label: a.name,
          group: threadCopy.composer.groups.agents,
          icon: <Avatar name={a.name} kind="agent" size="sm" />,
          meta: `@${a.id}`,
          insert,
          mention: { id: a.id, name: a.name, kind: 'agent' },
        }
      })
    // User mentions are first-class (D3): picking one records the ref so it
    // survives reconcile and reaches the backend as a mentioned_usernames
    // entry. The source is the topic's user participants; a mentioned user
    // gains a participant row server-side, so the roster grows on first
    // mention.
    const userOptions = users
      .filter((u) => u.toLowerCase().includes(q))
      .map<MentionOption>((u) => {
        const insert = formatMention(u)
        return {
          id: `user:${insert}`,
          label: u,
          group: threadCopy.composer.groups.people,
          icon: <Avatar name={u} kind="human" size="sm" />,
          insert,
          mention: { id: u, name: u, kind: 'user' },
        }
      })
    return [...agentOptions, ...userOptions]
  }
  if (active.trigger === '/') {
    return filterSlashCommands(active.query).map<MentionOption>((c) => {
      const insert = c.usage ? `/${c.name} ` : `/${c.name}`
      return {
        id: `command:${insert}`,
        label: c.name,
        group: threadCopy.composer.groups.commands,
        meta: c.description,
        insert,
      }
    })
  }
  return resources
    .filter((r) => r.name.toLowerCase().includes(q))
    .map<MentionOption>((r) => {
      const insert = formatResource(r.name)
      return {
        id: `resource:${insert}`,
        label: r.name,
        group: threadCopy.composer.groups.resources,
        meta: r.kind,
        insert,
        resource: r,
      }
    })
}
